import { ConnectProto } from "@racoon/api";
import { TFn } from "@racoon/i18n";
import type { GroupedRowMarkers } from "@racoon/ui";
import { humanTruckType, isShiftPlanned, isShiftRunning } from ".";
import { truckTypeOf } from "~/shared/lib/truck-type";
import type { ShiftData, ShiftDriverDetails } from "../model/shift-data";

/**
 * State of a truck's day, which is what orders the cascade: trucks with a shift
 * running come first, then ones still waiting for approval, then ones whose
 * shifts have all finished.
 */
type ShiftGroupTone = "active" | "planned" | "ended";

export type TruckShiftGroup = {
  kind: "truck";
  key: string;
  truckId?: bigint;
  /** The fleet record, when this group's truck is still in the active list. */
  truck?: ConnectProto.Truck;
  plateNumber: string;
  /** Manufacturer + model, e.g. "Iveco EuroCargo ML-800". */
  model: string;
  typeLabel: string;
  /** Owning sub-organization, e.g. "ПрУ АЛМ". */
  division: string;
  shifts: ShiftData[];
  /** Shifts in the group — the count shown on the header chip. */
  totalCount: number;
  tone: ShiftGroupTone;
};

/** Called only with a non-empty group — a truck without shifts never reaches the cascade. */
const toneOf = (shifts: ShiftData[]): ShiftGroupTone => {
  if (shifts.some((s) => isShiftRunning(s.status))) return "active";
  if (shifts.some((s) => isShiftPlanned(s.status))) return "planned";
  return "ended";
};

const TONE_ORDER: Record<ShiftGroupTone, number> = {
  active: 0,
  planned: 1,
  ended: 2,
};

/** `ShiftData.type` carries the enum's *number* as a string, so resolve the name first. */
export const shiftTypeLabel = (type: string | undefined, t: TFn): string => {
  const num = Number(type);
  if (!type || Number.isNaN(num)) return humanTruckType(undefined, t);
  return humanTruckType(ConnectProto.TruckTypeSchema.values.find((v) => v.number === num)?.name, t);
};

/** Shifts by `keyOf`, each bucket in input order, buckets in order of first appearance. */
const bucketShifts = (shifts: ShiftData[], keyOf: (shift: ShiftData) => string) => {
  const buckets = new Map<string, ShiftData[]>();
  for (const shift of shifts) {
    const key = keyOf(shift);
    const bucket = buckets.get(key);
    if (bucket) bucket.push(shift);
    else buckets.set(key, [shift]);
  }
  return buckets;
};

export const truckModelLabel = (truck: ConnectProto.Truck): string =>
  [truck.truckType?.manufacturer, truck.truckType?.model ?? truck.model]
    .filter(Boolean)
    .join(" ")
    .trim();

/**
 * Builds one cascade section per truck that has shifts in the selected range.
 *
 * Trucks lead, so the header carries the fleet's data (model, type, division),
 * but a vehicle with nothing scheduled is left out rather than shown as an
 * empty `0` group. Shifts whose truck is missing from `trucks` (an inactive
 * vehicle that still has history in the range) keep a group of their own rather
 * than vanishing from the screen.
 */
export const buildTruckShiftGroups = ({
  trucks,
  shifts,
  t,
}: {
  trucks: ConnectProto.Truck[];
  shifts: ShiftData[];
  t: TFn;
}): TruckShiftGroup[] => {
  const shiftsByTruck = bucketShifts(shifts, (shift) =>
    shift.truckId != null ? String(shift.truckId) : `plate:${shift.plateNumber}`,
  );

  const groups: TruckShiftGroup[] = trucks.flatMap((truck) => {
    const key = String(truck.id);
    const truckShifts = shiftsByTruck.get(key);
    if (!truckShifts) return [];
    shiftsByTruck.delete(key);

    return {
      kind: "truck" as const,
      key,
      truckId: truck.id,
      truck,
      plateNumber: truck.plateNumber,
      model: truckModelLabel(truck),
      typeLabel: humanTruckType(truckTypeOf(truck), t),
      division: truck.ownerOrganization?.name ?? "",
      shifts: truckShifts,
      totalCount: truckShifts.length,
      tone: toneOf(truckShifts),
    };
  });

  // Whatever is left belongs to trucks outside the active list.
  for (const [key, truckShifts] of shiftsByTruck) {
    const [first] = truckShifts;
    groups.push({
      kind: "truck",
      key,
      truckId: first?.truckId,
      plateNumber: first?.plateNumber ?? "",
      model: first?.model ?? "",
      typeLabel: shiftTypeLabel(first?.type, t),
      division: "",
      shifts: truckShifts,
      totalCount: truckShifts.length,
      tone: toneOf(truckShifts),
    });
  }

  return groups.sort(
    (a, b) => TONE_ORDER[a.tone] - TONE_ORDER[b.tone] || a.plateNumber.localeCompare(b.plateNumber),
  );
};

export type DriverShiftGroup = {
  kind: "driver";
  key: string;
  /** Unset on the group that collects shifts nobody is assigned to yet. */
  driverId?: bigint;
  driverName: string;
  details: ShiftDriverDetails;
  shifts: ShiftData[];
  totalCount: number;
  tone: ShiftGroupTone;
};

/** Key of the group holding shifts planned before anyone was assigned to them. */
const NO_DRIVER_KEY = "no-driver";

/**
 * Builds one cascade section per driver that has shifts in the selected range.
 * Shifts nobody is assigned to yet share one group, which always comes last.
 */
export const buildDriverShiftGroups = ({ shifts }: { shifts: ShiftData[] }): DriverShiftGroup[] => {
  const shiftsByDriver = bucketShifts(shifts, (shift) =>
    shift.driverId != null ? String(shift.driverId) : NO_DRIVER_KEY,
  );

  const groups = [...shiftsByDriver].map(([key, driverShifts]): DriverShiftGroup => {
    const [first] = driverShifts;
    return {
      kind: "driver",
      key,
      driverId: first?.driverId,
      driverName: first?.driverName ?? "",
      details: first?.driverDetails ?? {},
      shifts: driverShifts,
      totalCount: driverShifts.length,
      tone: toneOf(driverShifts),
    };
  });

  // Same order as the truck view, by surname instead of plate; "ru" keeps
  // Cyrillic collation the same on every machine.
  return groups.sort(
    (a, b) =>
      Number(a.key === NO_DRIVER_KEY) - Number(b.key === NO_DRIVER_KEY) ||
      TONE_ORDER[a.tone] - TONE_ORDER[b.tone] ||
      (a.details.lastName ?? "").localeCompare(b.details.lastName ?? "", "ru") ||
      a.driverName.localeCompare(b.driverName, "ru"),
  );
};

export type ShiftGroup = TruckShiftGroup | DriverShiftGroup;

/**
 * One row of the shifts table: either a real shift or the synthetic header that
 * introduces a group. `DataTable` sees a single flat list, which is what keeps
 * one column header above every group.
 */
export type ShiftRow = ShiftData &
  GroupedRowMarkers & {
    /** Present only on group header rows. */
    _group?: ShiftGroup;
  };

/** Placeholder payload for a header row — its cells are never rendered. */
const HEADER_ROW_FIELDS: ShiftData = {
  id: "0",
  status: ConnectProto.DriverOnShiftStatus.NOT_APPROVED,
  plateNumber: "",
  driverName: "",
  type: "",
  model: "",
  releasedAt: "",
  mileageBefore: 0,
};

/** Interleaves each group's header row with its shifts. */
export const flattenShiftGroups = (groups: ShiftGroup[]): ShiftRow[] =>
  groups.flatMap((group) => {
    const header: ShiftRow = {
      ...HEADER_ROW_FIELDS,
      _isGroupHeader: true,
      _groupKey: group.key,
      _group: group,
    };

    return [
      header,
      ...group.shifts.map<ShiftRow>((shift, index) => ({
        ...shift,
        _groupKey: group.key,
        _isLastInGroup: index === group.shifts.length - 1,
      })),
    ];
  });
